import React, { useCallback, useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import Tooltip from "@mui/material/Tooltip";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Badge from "@mui/material/Badge";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import AddLocationAltIcon from "@mui/icons-material/AddLocationAlt";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import VideocamIcon from "@mui/icons-material/Videocam";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import toast from "react-hot-toast";
import { ApiRequestError } from "../../api/client.ts";
import {
  adminNodeCreateCamera,
  adminNodeDeleteCamera,
  adminNodeListCameras,
  adminNodePutRecordingSchedule,
  adminNodeUpdateCamera,
  adminRowFromServerCamera,
  resolveAdminCameraCreatedAt,
  resolveAdminCameraRowId,
} from "../../api/adminNodeCameras.ts";
import { GLOBAL_TOOLTIP_PROPS } from "../../config/tooltips.config.ts";
import { ViewToggle } from "../../components/aiDetectionSections/ViewToggle.tsx";
import type { ViewMode } from "../../components/aiDetectionSections/ViewToggle.tsx";
import {
  AdminOmniWatchAddCameraDialog,
  adminOmniRowRecordToFormValues,
  buildAdminOmniCameraRequestBody,
  mapAdminCameraApiErrorToFields,
  type AdminOmniCameraFormValues,
  type AdminOmniCameraSubmitResult,
} from "./AdminOmniWatchAddCameraDialog.tsx";
import { AdministrationSectionHeader } from "./AdministrationSectionHeader.tsx";
import { AdminCameraHotspotDialog } from "./AdminCameraHotspotDialog.tsx";
import { useModuleCrudPermissions } from "../../hooks/useModuleCrudPermissions.js";

type HotspotDialogMode = "hotspot" | "preset";

const CAM_FILTER_SELECT_PR_CLEAR = "76px";
const CAM_FILTER_CLEAR_BTN_RIGHT_PX = 36;
const LS_VIEW_MODE = "administration-camera-mgmt-view-mode";
const LS_FAVORITE_IDS = "administration-camera-mgmt-favorite-ids";

function cell(v: unknown): string {
  if (v == null || v === "") return "";
  return String(v);
}

function formatOnvifDisplay(c: Record<string, unknown>): string {
  const host = String(
    c.onvif_host ?? c.onvifHost ?? c.onvif_ip ?? c.camera_ip ?? "",
  ).trim();
  const portRaw = c.onvif_port ?? c.onvifPort ?? c.onvif_http_port;
  const port =
    portRaw != null && String(portRaw).trim() !== ""
      ? String(portRaw).trim()
      : "";
  if (!host) return "—";
  return port ? `${host}:${port}` : host;
}

function onvifConfigured(c: Record<string, unknown>): boolean {
  return formatOnvifDisplay(c) !== "—";
}

function rowId(c: Record<string, unknown>): string {
  return cell(c.id || c.camera_id).trim();
}

function loadFavoriteIds(): Set<string> {
  try {
    const raw = localStorage.getItem(LS_FAVORITE_IDS);
    if (!raw) return new Set();
    const arr = JSON.parse(raw) as unknown;
    if (Array.isArray(arr)) return new Set(arr.map(String));
  } catch {
    /* ignore */
  }
  return new Set();
}

function saveFavoriteIds(ids: Set<string>) {
  try {
    localStorage.setItem(LS_FAVORITE_IDS, JSON.stringify([...ids]));
  } catch {
    /* ignore */
  }
}

function loadViewMode(): ViewMode {
  try {
    const v = localStorage.getItem(LS_VIEW_MODE);
    if (v === "grid" || v === "list") return v;
  } catch {
    /* ignore */
  }
  return "list";
}

function rowMatchesDateFilter(
  c: Record<string, unknown>,
  filterDateRange: string,
): boolean {
  if (filterDateRange === "all") return true;
  const raw = c.created_at ?? c.createdAt;
  if (raw == null || String(raw).trim() === "") return false;
  const date = new Date(String(raw)).getTime();
  if (Number.isNaN(date)) return false;
  const end = Date.now();
  let start: number;
  if (filterDateRange === "last7days") {
    start = new Date().setDate(new Date().getDate() - 7);
  } else if (filterDateRange === "last30days") {
    start = new Date().setDate(new Date().getDate() - 30);
  } else if (filterDateRange === "last3months") {
    start = new Date().setMonth(new Date().getMonth() - 3);
  } else {
    return true;
  }
  return date >= start && date <= end;
}

/** Build a table row from the form (same shape as `buildAdminOmniCameraRequestBody` + id + timestamp). */
function cameraRowFromForm(
  id: string,
  values: AdminOmniCameraFormValues,
  createdAtIso: string,
): Record<string, unknown> {
  const payload = buildAdminOmniCameraRequestBody(values);
  return {
    id,
    camera_id: id,
    ...payload,
    recording_schedule_slots: values.recordingScheduleSlots,
    recording_schedule_timezone: values.recordingScheduleTimezone,
    created_at: createdAtIso,
  };
}

function newLocalCameraId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `cam-${Date.now()}`;
}

/**
 * Administration → Camera Management — list and CRUD via GET/POST/PUT/DELETE /api/cameras.
 * Loads and saves via Node API `GET | POST | PUT .../api/cameras` (see {@link ../../api/adminNodeCameras.ts}).
 */
export function AdministrationCameraManagement() {
  const { create: canCreate, update: canUpdate, delete: canDelete, hasRowActions } =
    useModuleCrudPermissions("cameras");
  const [cameras, setCameras] = useState<Record<string, unknown>[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editSourceRow, setEditSourceRow] = useState<Record<
    string,
    unknown
  > | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuRow, setMenuRow] = useState<Record<string, unknown> | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>(loadViewMode);
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  const [favoriteIds, setFavoriteIds] = useState<Set<string>>(loadFavoriteIds);
  const [filterDateRange, setFilterDateRange] = useState("all");
  const [onvifFilter, setOnvifFilter] = useState<string>("all");
  const [hotspotDialogOpen, setHotspotDialogOpen] = useState(false);
  const [hotspotDialogMode, setHotspotDialogMode] =
    useState<HotspotDialogMode>("hotspot");
  const [hotspotCameraId, setHotspotCameraId] = useState("");
  const [hotspotCameraName, setHotspotCameraName] = useState("");
  const [hotspotCameraHas360, setHotspotCameraHas360] = useState(false);
  const [hotspotCameraHasPtz, setHotspotCameraHasPtz] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(LS_VIEW_MODE, viewMode);
    } catch {
      /* ignore */
    }
  }, [viewMode]);

  useEffect(() => {
    saveFavoriteIds(favoriteIds);
  }, [favoriteIds]);

  useEffect(() => {
    const t = window.setTimeout(() => setSearchQuery(search), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const loadCameras = useCallback(async (signal?: AbortSignal) => {
    setLoading(true);
    setLoadError(null);
    try {
      const rows = await adminNodeListCameras(
        searchQuery.trim() || undefined,
        signal,
      );
      setCameras(rows);
    } catch (e) {
      if (signal?.aborted) return;
      const msg =
        e instanceof ApiRequestError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Could not load cameras";
      setLoadError(msg);
      setCameras([]);
      toast.error(msg);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [searchQuery]);

  useEffect(() => {
    const ac = new AbortController();
    void loadCameras(ac.signal);
    return () => ac.abort();
  }, [loadCameras]);

  const dialogInitialValues = useMemo(
    () =>
      editSourceRow && dialogOpen
        ? adminOmniRowRecordToFormValues(editSourceRow)
        : null,
    [editSourceRow, dialogOpen],
  );

  const favoritesCount = useMemo(
    () => cameras.filter((c) => favoriteIds.has(rowId(c))).length,
    [cameras, favoriteIds],
  );

  const filtered = useMemo(() => {
    let list = [...cameras];

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((c) => {
        const name = cell(c.name).toLowerCase();
        const a360 = cell(
          c.cam360_rtsp ?? c.streaming_url ?? c.stream_url,
        ).toLowerCase();
        const ptz = cell(c.ptz_rtsp).toLowerCase();
        const onv = formatOnvifDisplay(c).toLowerCase();
        return (
          name.includes(q) ||
          a360.includes(q) ||
          ptz.includes(q) ||
          onv.includes(q)
        );
      });
    }

    if (showFavoritesOnly) {
      list = list.filter((c) => favoriteIds.has(rowId(c)));
    }

    if (filterDateRange !== "all") {
      list = list.filter((c) => rowMatchesDateFilter(c, filterDateRange));
    }

    if (onvifFilter === "configured") {
      list = list.filter((c) => onvifConfigured(c));
    } else if (onvifFilter === "none") {
      list = list.filter((c) => !onvifConfigured(c));
    }

    return list;
  }, [
    cameras,
    search,
    showFavoritesOnly,
    favoriteIds,
    filterDateRange,
    onvifFilter,
  ]);

  const pageRows = useMemo(
    () => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filtered, page, rowsPerPage],
  );

  const handleToggleFavorite = useCallback(
    (id: string, e?: React.MouseEvent) => {
      e?.stopPropagation();
      setFavoriteIds((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id);
        else next.add(id);
        return next;
      });
    },
    [],
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setEditSourceRow(null);
  }, []);

  const handleDialogSubmit = useCallback(
    async (
      values: AdminOmniCameraFormValues,
    ): Promise<AdminOmniCameraSubmitResult> => {
      const payload = buildAdminOmniCameraRequestBody(values);
      const existingId = editSourceRow ? rowId(editSourceRow) : "";
      const shouldSaveSchedule =
        values.recordingEnabled &&
        values.recordingType === "scheduled" &&
        values.recordingScheduleSlots !== null;
      try {
        if (existingId) {
          const created = cell(editSourceRow?.created_at);
          const createdAtIso = created || new Date().toISOString();
          const id = existingId;
          if (shouldSaveSchedule && values.recordingScheduleSlots) {
            await adminNodePutRecordingSchedule(id, {
              slots: values.recordingScheduleSlots,
            });
          }
          const serverRow = await adminNodeUpdateCamera(id, payload);
          const createdResolved = resolveAdminCameraCreatedAt(
            serverRow,
            createdAtIso,
          );
          const merged = {
            ...adminRowFromServerCamera(serverRow ?? {}),
            ...cameraRowFromForm(id, values, createdResolved),
          };
          setCameras((prev) =>
            prev.map((c) => (rowId(c) === existingId ? merged : c)),
          );
          toast.success("Camera updated");
        } else {
          const provisionalId = newLocalCameraId();
          const serverRow = await adminNodeCreateCamera(payload);
          const id = resolveAdminCameraRowId(serverRow, provisionalId);
          if (shouldSaveSchedule && values.recordingScheduleSlots) {
            await adminNodePutRecordingSchedule(id, {
              slots: values.recordingScheduleSlots,
            });
          }
          const createdResolved = resolveAdminCameraCreatedAt(
            serverRow,
            new Date().toISOString(),
          );
          const merged = {
            ...adminRowFromServerCamera(serverRow ?? {}),
            ...cameraRowFromForm(id, values, createdResolved),
          };
          setCameras((prev) => [...prev, merged]);
          toast.success("Camera saved");
        }
        closeDialog();
        await loadCameras();
        return { ok: true };
      } catch (e) {
        const msg =
          e instanceof ApiRequestError
            ? e.message
            : e instanceof Error
              ? e.message
              : "Could not save camera";
        toast.error(msg);
        return {
          ok: false,
          message: msg,
          fieldErrors: mapAdminCameraApiErrorToFields(msg),
        };
      }
    },
    [editSourceRow, loadCameras, closeDialog],
  );

  const handleDelete = useCallback(
    async (cameraId: string) => {
      if (!window.confirm("Delete this camera? This cannot be undone.")) {
        return;
      }
      try {
        await adminNodeDeleteCamera(cameraId);
        setCameras((prev) => prev.filter((c) => rowId(c) !== cameraId));
        setFavoriteIds((prev) => {
          const next = new Set(prev);
          next.delete(cameraId);
          return next;
        });
        setMenuAnchor(null);
        setMenuRow(null);
        toast.success("Camera deleted");
        await loadCameras();
      } catch (e) {
        const msg =
          e instanceof ApiRequestError
            ? e.message
            : e instanceof Error
              ? e.message
              : "Could not delete camera";
        toast.error(msg);
      }
    },
    [loadCameras],
  );

  const openAdd = () => {
    setEditSourceRow(null);
    setDialogOpen(true);
  };

  const openEdit = (c: Record<string, unknown>) => {
    const id = rowId(c);
    if (!id) return;
    setEditSourceRow(c);
    setDialogOpen(true);
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const openHotspotDialog = (
    c: Record<string, unknown>,
    mode: HotspotDialogMode = "hotspot",
  ) => {
    const id = rowId(c);
    if (!id) return;
    const stream360 = cell(
      c.cam360_rtsp ?? c.cam360Rtsp ?? c.streaming_url ?? c.stream_url,
    );
    const streamPtz = cell(c.ptz_rtsp ?? c.ptzRtsp);
    setHotspotCameraId(id);
    setHotspotCameraName(cell(c.name));
    setHotspotCameraHas360(Boolean(stream360.trim()));
    setHotspotCameraHasPtz(Boolean(streamPtz.trim()));
    setHotspotDialogMode(mode);
    setHotspotDialogOpen(true);
    setMenuAnchor(null);
    setMenuRow(null);
  };

  const emptyDataset = !loading && !loadError && cameras.length === 0;
  const emptyFiltered =
    !loading && !loadError && cameras.length > 0 && filtered.length === 0;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        flex: 1,
        minHeight: 0,
        width: "100%",
      }}
    >
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          flex: 1,
        }}
      >
        <Paper
          elevation={1}
          sx={{
            minHeight: 480,
            height: "auto",
            maxHeight: "none",
            flex: 1,
            bgcolor: "background.paper",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "20px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            position: "relative",
          }}
        >
          <AdministrationSectionHeader
            title="Camera Management"
            infoTooltip="Configure 360° and PTZ RTSP URLs, ONVIF control, and optional PTZ calibration. Cameras load from your Node backend at `GET /api/cameras` (`REACT_APP_NODE_API_URL`, default http://localhost:5000)."
          />
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              px: { xs: 1, sm: 2 },
              pb: 1,
              fontFamily: "Roboto, sans-serif",
            }}
          ></Typography>

          {/* Toolbar — matches Manage Use Case / AI Models Management row */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: 1,
              px: { xs: 1, sm: 2 },
              pb: 2,
              alignItems: "center",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <TextField
              size="small"
              placeholder="Search camera..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              sx={{
                width: { xs: "100%", sm: "240px" },
                minWidth: { xs: 0, sm: 240 },
                flex: { xs: "1 1 100%", sm: "0 0 auto" },
                "& .MuiOutlinedInput-root": {
                  height: 36,
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  bgcolor: "#fff",
                  borderRadius: "10px",
                  "& fieldset": {
                    borderColor: "#E5E7EB",
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 20, color: "#9E9E9E" }} />
                  </InputAdornment>
                ),
              }}
            />

            <Tooltip
              title={
                showFavoritesOnly ? "Show all cameras" : "Show favorites only"
              }
              {...GLOBAL_TOOLTIP_PROPS}
            >
              <IconButton
                size="small"
                onClick={() => {
                  setShowFavoritesOnly(!showFavoritesOnly);
                  setPage(0);
                }}
                sx={{
                  border: "1px solid #E5E7EB",
                  borderRadius: "10px",
                  height: 36,
                  width: 36,
                  bgcolor: showFavoritesOnly ? "#FFE0D0" : "#fff",
                  "&:hover": {
                    bgcolor: showFavoritesOnly ? "#FFD0B8" : "#F5F5F5",
                  },
                }}
              >
                <Badge
                  badgeContent={favoritesCount}
                  color="primary"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "10px",
                      height: "16px",
                      minWidth: "16px",
                      bgcolor: "#2932E5",
                      borderRadius: "10px",
                    },
                  }}
                >
                  {showFavoritesOnly ? (
                    <StarIcon fontSize="small" sx={{ color: "#FF8555" }} />
                  ) : (
                    <StarBorderIcon
                      fontSize="small"
                      sx={{ color: "#9E9E9E" }}
                    />
                  )}
                </Badge>
              </IconButton>
            </Tooltip>

            <ViewToggle
              view={viewMode}
              onChange={(v) => {
                setViewMode(v);
                setPage(0);
              }}
              ariaLabel="Toggle cameras view mode"
            />

            <FormControl
              size="small"
              sx={{ minWidth: { xs: 100, sm: 120 }, position: "relative" }}
            >
              <Select
                value={filterDateRange}
                onChange={(e) => {
                  setFilterDateRange(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                sx={{
                  height: 36,
                  minWidth: { xs: 100, sm: 120 },
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  bgcolor: filterDateRange !== "all" ? "#FFE0D0" : "#fff",
                  borderRadius: "10px",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingRight:
                      filterDateRange !== "all"
                        ? `${CAM_FILTER_SELECT_PR_CLEAR} !important`
                        : undefined,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover": {
                    bgcolor: filterDateRange !== "all" ? "#FFE0D0" : "#f5f5f5",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2932E5",
                  },
                }}
                startAdornment={
                  <CalendarTodayIcon
                    sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }}
                  />
                }
              >
                <MenuItem value="all">All Dates</MenuItem>
                <MenuItem value="last7days">Last 7 Days</MenuItem>
                <MenuItem value="last30days">Last 30 Days</MenuItem>
                <MenuItem value="last3months">Last 3 Months</MenuItem>
              </Select>
              {filterDateRange !== "all" ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setFilterDateRange("all");
                    setPage(0);
                  }}
                  aria-label="Clear date filter"
                  sx={{
                    position: "absolute",
                    right: `${CAM_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    p: 0.25,
                    width: 28,
                    height: 28,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null}
            </FormControl>

            <FormControl
              size="small"
              sx={{ minWidth: { xs: 120, sm: 150 }, position: "relative" }}
            >
              <Select
                value={onvifFilter}
                onChange={(e) => {
                  setOnvifFilter(e.target.value);
                  setPage(0);
                }}
                displayEmpty
                sx={{
                  height: 36,
                  minWidth: { xs: 120, sm: 150 },
                  borderRadius: "10px",
                  fontFamily: "Roboto, sans-serif",
                  fontSize: "14px",
                  bgcolor: onvifFilter !== "all" ? "#FFE0D0" : "#fff",
                  "& .MuiSelect-select": {
                    display: "flex",
                    alignItems: "center",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    paddingRight:
                      onvifFilter !== "all"
                        ? `${CAM_FILTER_SELECT_PR_CLEAR} !important`
                        : undefined,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#E5E7EB",
                  },
                  "&:hover": {
                    bgcolor: onvifFilter !== "all" ? "#FFE0D0" : "#f5f5f5",
                  },
                  "&:hover .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#2932E5",
                  },
                }}
                startAdornment={
                  <FilterListIcon
                    sx={{ fontSize: 18, color: "#9E9E9E", mr: 1 }}
                  />
                }
              >
                <MenuItem value="all">All cameras</MenuItem>
                <MenuItem value="configured">ONVIF configured</MenuItem>
                <MenuItem value="none">No ONVIF</MenuItem>
              </Select>
              {onvifFilter !== "all" ? (
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setOnvifFilter("all");
                    setPage(0);
                  }}
                  aria-label="Clear ONVIF filter"
                  sx={{
                    position: "absolute",
                    right: `${CAM_FILTER_CLEAR_BTN_RIGHT_PX}px`,
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    p: 0.25,
                    width: 28,
                    height: 28,
                    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              ) : null}
            </FormControl>

            <Box sx={{ flex: 1 }} />

            {canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{
                bgcolor: "#2932E5",
                border: "0.8px solid #2932E5",
                borderRadius: "10px",
                color: "#fff",
                textTransform: "none",
                fontFamily: "Roboto, sans-serif",
                fontSize: { xs: "13px", sm: "14px" },
                fontWeight: 500,
                height: 36,
                px: { xs: 1.5, sm: 2 },
                boxShadow: "none",
                flexShrink: 0,
                "&:hover": {
                  bgcolor: "#1f28b8",
                  border: "0.8px solid #1f28b8",
                  boxShadow: "none",
                },
              }}
            >
              Add Camera
            </Button>
            ) : null}
          </Box>

          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {loading ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  bgcolor: "#FAFAFA",
                }}
              >
                <CircularProgress size={40} sx={{ color: "#2932E5" }} />
              </Box>
            ) : loadError ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  bgcolor: "#FAFAFA",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "16px",
                    color: "#6B7280",
                    textAlign: "center",
                  }}
                >
                  {loadError}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={() => void loadCameras()}
                  sx={{ textTransform: "none", borderRadius: "10px" }}
                >
                  Retry
                </Button>
              </Box>
            ) : emptyDataset ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  bgcolor: "#FAFAFA",
                }}
              >
                <VideocamIcon sx={{ fontSize: 64, color: "#9CA3AF", mb: 1 }} />
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#9CA3AF",
                    textAlign: "center",
                    mt: 0.5,
                  }}
                >
                  {canCreate
                    ? "Use Add Camera to create a new camera."
                    : "No cameras are configured yet."}
                </Typography>
              </Box>
            ) : emptyFiltered ? (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  p: 4,
                  bgcolor: "#FAFAFA",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "16px",
                    color: "#6B7280",
                    textAlign: "center",
                  }}
                >
                  No cameras match your filters.
                </Typography>
                <Typography
                  sx={{
                    fontFamily: "Roboto, sans-serif",
                    fontSize: "14px",
                    color: "#9CA3AF",
                    textAlign: "center",
                  }}
                >
                  Try adjusting your search or filters.
                </Typography>
              </Box>
            ) : viewMode === "list" ? (
              <TableContainer
                component={Box}
                sx={{ flex: 1, minHeight: 0, overflow: "auto" }}
              >
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#2932E5",
                          bgcolor: "#FAFAFA",
                          fontFamily: "Roboto, sans-serif",
                        }}
                      >
                        Camera Name
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#2932E5",
                          bgcolor: "#FAFAFA",
                          fontFamily: "Roboto, sans-serif",
                        }}
                      >
                        360° Streaming URL
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#2932E5",
                          bgcolor: "#FAFAFA",
                          fontFamily: "Roboto, sans-serif",
                        }}
                      >
                        PTZ Streaming URL
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#2932E5",
                          bgcolor: "#FAFAFA",
                          fontFamily: "Roboto, sans-serif",
                        }}
                      >
                        ONVIF
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 600,
                          color: "#2932E5",
                          bgcolor: "#FAFAFA",
                          fontFamily: "Roboto, sans-serif",
                        }}
                      >
                        Created Date
                      </TableCell>
                      {hasRowActions ? (
                      <TableCell
                        align="right"
                        sx={{
                          fontWeight: 600,
                          color: "#2932E5",
                          bgcolor: "#FAFAFA",
                          width: 56,
                        }}
                      />
                      ) : null}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pageRows.map((c) => {
                      const id = rowId(c);
                      const name = cell(c.name);
                      const u360 = cell(
                        c.cam360_rtsp ?? c.streaming_url ?? c.stream_url,
                      );
                      const uPtz = cell(c.ptz_rtsp);
                      const createdRaw = c.created_at ?? c.createdAt;
                      const created = createdRaw
                        ? new Date(String(createdRaw)).toLocaleString()
                        : "—";
                      const onv = formatOnvifDisplay(c);
                      const fav = id ? favoriteIds.has(id) : false;
                      return (
                        <TableRow key={id || name} hover>
                          <TableCell>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                minWidth: 0,
                              }}
                            >
                              <VideocamIcon
                                fontSize="small"
                                sx={{ color: "#9CA3AF", flexShrink: 0 }}
                              />
                              <Typography
                                noWrap
                                sx={{ fontSize: 14, maxWidth: 180, flex: 1 }}
                              >
                                {name || "—"}
                              </Typography>
                              {id ? (
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleToggleFavorite(id, e)}
                                  aria-label={
                                    fav
                                      ? "Remove from favorites"
                                      : "Add to favorites"
                                  }
                                  sx={{ flexShrink: 0, p: 0.25 }}
                                >
                                  {fav ? (
                                    <StarIcon
                                      sx={{ fontSize: 18, color: "#FF8555" }}
                                    />
                                  ) : (
                                    <StarBorderIcon
                                      sx={{ fontSize: 18, color: "#9CA3AF" }}
                                    />
                                  )}
                                </IconButton>
                              ) : null}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Ellipsis text={u360 || "—"} />
                          </TableCell>
                          <TableCell sx={{ maxWidth: 280 }}>
                            <Ellipsis text={uPtz || "—"} />
                          </TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: 13 }}>{onv}</Typography>
                          </TableCell>
                          <TableCell
                            sx={{ whiteSpace: "nowrap", fontSize: 13 }}
                          >
                            {created}
                          </TableCell>
                          {hasRowActions ? (
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              aria-label="Row actions"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMenuAnchor(e.currentTarget);
                                setMenuRow(c);
                              }}
                            >
                              <MoreVertIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                  p: 2,
                  bgcolor: "#FAFAFA",
                }}
              >
                <Grid container spacing={2}>
                  {pageRows.map((c) => {
                    const id = rowId(c);
                    const name = cell(c.name);
                    const u360 = cell(
                      c.cam360_rtsp ?? c.streaming_url ?? c.stream_url,
                    );
                    const uPtz = cell(c.ptz_rtsp);
                    const createdRaw = c.created_at ?? c.createdAt;
                    const created = createdRaw
                      ? new Date(String(createdRaw)).toLocaleString()
                      : "—";
                    const onv = formatOnvifDisplay(c);
                    const fav = id ? favoriteIds.has(id) : false;
                    return (
                      <Grid item xs={12} sm={6} md={4} key={id || name}>
                        <Card
                          variant="outlined"
                          sx={{
                            borderRadius: "14px",
                            height: "100%",
                            borderColor: "#E5E7EB",
                            bgcolor: "#fff",
                          }}
                        >
                          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "flex-start",
                                justifyContent: "space-between",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.75,
                                  minWidth: 0,
                                  flex: 1,
                                }}
                              >
                                <VideocamIcon
                                  fontSize="small"
                                  sx={{ color: "#9CA3AF", flexShrink: 0 }}
                                />
                                <Typography
                                  fontWeight={600}
                                  noWrap
                                  sx={{ fontSize: 15 }}
                                >
                                  {name || "—"}
                                </Typography>
                              </Box>
                              <Box sx={{ display: "flex", gap: 0.25 }}>
                                {id ? (
                                  <IconButton
                                    size="small"
                                    onClick={(e) => handleToggleFavorite(id, e)}
                                    aria-label={
                                      fav
                                        ? "Remove from favorites"
                                        : "Add to favorites"
                                    }
                                  >
                                    {fav ? (
                                      <StarIcon
                                        sx={{ fontSize: 20, color: "#FF8555" }}
                                      />
                                    ) : (
                                      <StarBorderIcon
                                        sx={{ fontSize: 20, color: "#9CA3AF" }}
                                      />
                                    )}
                                  </IconButton>
                                ) : null}
                                {hasRowActions ? (
                                <IconButton
                                  size="small"
                                  aria-label="Row actions"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setMenuAnchor(e.currentTarget);
                                    setMenuRow(c);
                                  }}
                                >
                                  <MoreVertIcon fontSize="small" />
                                </IconButton>
                                ) : null}
                              </Box>
                            </Box>
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{ mb: 0.5 }}
                            >
                              360°
                            </Typography>
                            <Ellipsis text={u360 || "—"} />
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{ mt: 1, mb: 0.5 }}
                            >
                              PTZ
                            </Typography>
                            <Ellipsis text={uPtz || "—"} />
                            <Typography
                              variant="body2"
                              sx={{ mt: 1.5, fontSize: 13 }}
                            >
                              ONVIF: {onv}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ display: "block", mt: 0.5 }}
                            >
                              {created}
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              </Box>
            )}

            {!emptyDataset && !emptyFiltered ? (
              <TablePagination
                component="div"
                count={filtered.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[5, 10, 25]}
                sx={{
                  borderTop: "1px solid #E5E7EB",
                  "& .MuiTablePagination-toolbar": { minHeight: 52 },
                }}
              />
            ) : null}
          </Box>
        </Paper>
      </motion.div>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor) && Boolean(menuRow)}
        onClose={() => {
          setMenuAnchor(null);
          setMenuRow(null);
        }}
      >
        {canUpdate ? (
        <MenuItem
          onClick={() => {
            if (menuRow) openEdit(menuRow);
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <EditOutlinedIcon fontSize="small" sx={{ color: "text.primary" }} />
          </ListItemIcon>
          Edit
        </MenuItem>
        ) : null}
        {canUpdate ? (
        <MenuItem
          onClick={() => {
            if (menuRow) openHotspotDialog(menuRow);
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <AddLocationAltIcon fontSize="small" sx={{ color: "text.primary" }} />
          </ListItemIcon>
          Add hotspot
        </MenuItem>
        ) : null}
        {canDelete ? (
          <>
            <MenuItem
              onClick={() => {
                if (menuRow) openHotspotDialog(menuRow, "preset");
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <GridViewOutlinedIcon
                  fontSize="small"
                  sx={{ color: "text.primary" }}
                />
              </ListItemIcon>
              Manage presets
            </MenuItem>
            <MenuItem
              onClick={() => {
                const id = menuRow ? rowId(menuRow) : "";
                setMenuAnchor(null);
                setMenuRow(null);
                if (id) void handleDelete(id);
              }}
              sx={{ color: "error.main" }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: "error.main" }}>
                <DeleteOutlinedIcon fontSize="small" />
              </ListItemIcon>
              Delete
            </MenuItem>
          </>
        ) : null}
      </Menu>

      <AdminOmniWatchAddCameraDialog
        open={dialogOpen}
        onClose={closeDialog}
        initialValues={dialogInitialValues}
        editCameraId={
          editSourceRow && dialogOpen ? rowId(editSourceRow) || null : null
        }
        onSubmit={handleDialogSubmit}
      />

      <AdminCameraHotspotDialog
        open={hotspotDialogOpen}
        onClose={() => {
          setHotspotDialogOpen(false);
          setHotspotDialogMode("hotspot");
          setHotspotCameraId("");
          setHotspotCameraName("");
          setHotspotCameraHas360(false);
          setHotspotCameraHasPtz(false);
        }}
        cameraId={hotspotCameraId}
        cameraName={hotspotCameraName}
        has360Stream={hotspotCameraHas360}
        hasPtzStream={hotspotCameraHasPtz}
        initialMode={hotspotDialogMode}
      />
    </Box>
  );
}

function Ellipsis({ text }: { text: string }) {
  const showTip = text.length > 56;
  const short = showTip ? `${text.slice(0, 53)}…` : text;
  const body = (
    <Typography
      sx={{
        fontSize: 13,
        color: "text.secondary",
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {short}
    </Typography>
  );
  return showTip ? (
    <Tooltip title={text}>
      <span>{body}</span>
    </Tooltip>
  ) : (
    body
  );
}
